import { useAtomValue } from "jotai";
import {
  isActiveUserArtistAtom,
  isActiveUserEngineerOrStudioManagerAtom,
} from "../../../atoms/user/activeUserAtom";
import { TRACKING_EVENTS_NAME } from "../../../constants/trackingEvents";
import { SCREENS } from "../../../routes/screens";
import { AccountType } from "../../../store/actions/dashboard";
import { useAppSelector } from "../../../store/hooks";
import { SUBSCRIPTION_PLAN } from "../../../store/models/subscription";
import {
  isEngineerOnboardedSelector,
  selectUserCanEnableStudioServices,
} from "../../../store/selectors/userInfoSelectors";
import { Text, TEXT_WEIGHT } from "../../core-ui/components/Text/Text";
import { TextStyleVariant } from "../../core-ui/components/Text/TextUtils";
import { ManageSubscriptionCardContainer } from "./ManageSubscriptionCard.styles";
import { ManageSubscriptionSection } from "./ManageSubscriptionSection";

export interface ManageSubscriptionCardProps {
  feesAvoided?: number;
  currency?: string;
  onClose?: () => void;
}

export const ManageSubscriptionCard = ({
  currency,
  feesAvoided,
  onClose,
}: ManageSubscriptionCardProps) => {
  const isArtist = useAtomValue(isActiveUserArtistAtom);
  const isServiceProvider = useAtomValue(
    isActiveUserEngineerOrStudioManagerAtom,
  );
  const isOnboardedEngineer = useAppSelector(isEngineerOnboardedSelector);
  const canEnableStudioServices = useAppSelector(
    selectUserCanEnableStudioServices,
  );

  if (
    (!isArtist && !isServiceProvider) ||
    // We don't want to show the card if the user is not onboarded as an engineer and not as a studio manager
    (!isOnboardedEngineer && !canEnableStudioServices && !isArtist)
  )
    return null;

  return (
    <ManageSubscriptionCardContainer>
      <Text variant={TextStyleVariant.P1} weight={TEXT_WEIGHT.SEMI_BOLD}>
        Membership Status
      </Text>
      {isServiceProvider && (
        <>
          <Text bold>Service Provider Subscription</Text>
          <ManageSubscriptionSection
            nonPlatinumSubscriptionPlans={[
              SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN,
              SUBSCRIPTION_PLAN.ESSENTIALS_PLAN,
              SUBSCRIPTION_PLAN.LEGACY_BASIC_PLAN,
            ]}
            subscriptionRoute={SCREENS.ADD_SUBSCRIPTION}
            onClose={onClose}
            trackingEventName={
              TRACKING_EVENTS_NAME.CLICK_MANAGE_SUBSCRIPTION_ON_CARD
            }
            accountType={AccountType.ENGINEER}
            feesAvoided={feesAvoided}
            currency={currency}
          />
        </>
      )}
      {isArtist && (
        <>
          <Text bold>Artist subscription</Text>
          <ManageSubscriptionSection
            nonPlatinumSubscriptionPlans={[
              SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN,
              SUBSCRIPTION_PLAN.ARTIST_ESSENTIALS_PLAN,
            ]}
            subscriptionRoute={SCREENS.ADD_ARTIST_SUBSCRIPTION}
            onClose={onClose}
            trackingEventName={
              TRACKING_EVENTS_NAME.CLICK_MANAGE_ARTIST_SUBSCRIPTION_ON_CARD
            }
            accountType={AccountType.ARTIST}
          />
        </>
      )}
    </ManageSubscriptionCardContainer>
  );
};
